/**
 * 计费单位名称缓存（measureId → 计费单位中文名）
 *
 * 费用行的「计费单位类型（billingUnitType）」只区分按箱/非按箱，
 * 非按箱时还需单位名称（计费重/重量/体积/票…）才能按后端口径换算金额，
 * 因此统一缓存批量基础数据（batch-base-data-provider）返回的计费单位名称。
 */
import { ref } from 'vue'
import { UniversalSearchDataType } from '@/enums/universal-search'

interface BillingUnitLike {
  id?: unknown
  nameCn?: unknown
  unitNameCn?: unknown
  measureNameCn?: unknown
  content?: {
    unitNameCn?: unknown
    measureNameCn?: unknown
  } | null
}

/** 从批量基础数据结果中提取 measureId → 计费单位中文名 */
export const collectBillingUnitNames = (batchData?: Record<string, any[]> | null): Record<string, string> => {
  const names: Record<string, string> = {}
  const unitList = (batchData?.[UniversalSearchDataType.BILLING_UNIT] || []) as BillingUnitLike[]
  if (!Array.isArray(unitList)) return names
  unitList.forEach(unit => {
    const id = String(unit?.id ?? '')
    // 批量接口返回 nameCn 即 unitNameCn；returnFullVO 时取 content 内的单位名
    const name = String(
      unit?.content?.unitNameCn || unit?.content?.measureNameCn ||
      unit?.unitNameCn || unit?.measureNameCn || unit?.nameCn || ''
    ).trim()
    if (id && name) names[id] = name
  })
  return names
}

/** 计费单位名称缓存：配合 BatchBaseDataProvider 的 loaded 事件使用 */
export const useBillingUnitNames = () => {
  const unitNameMap = ref<Record<string, string>>({})

  /** 基础数据加载完成时刷新缓存（增量合并，避免刷新时丢失已有名称） */
  const cacheUnitNamesFromBaseData = (batchData?: Record<string, any[]> | null) => {
    const names = collectBillingUnitNames(batchData)
    if (Object.keys(names).length) {
      unitNameMap.value = { ...unitNameMap.value, ...names }
    }
  }

  return { unitNameMap, cacheUnitNamesFromBaseData }
}
